import {ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useId, useState} from "react";

export function jsonPost(url: string, payload: any) {
    return fetch(url, {method: 'POST', headers: {"Content-Type": "application/json"}, body: JSON.stringify(payload)})
}

export function jsonPut(url: string, payload: any) {
    return fetch(url, {method: 'PUT', headers: {"Content-Type": "application/json"}, body: JSON.stringify(payload)})
}

// stolen from https://github.com/microsoft/TypeScript/issues/37663#issuecomment-606110995
export type Initializer<T> = T extends Function ? never : (T | (() => T))

export function useLocalStorageState<T>(key: string, fallbackState?: Initializer<T>) {
    return usePersistedState<T>(window.localStorage, key, fallbackState)
}

function usePersistedState<T>(store: Storage, key: string, fallbackState?: Initializer<T>): [T, Dispatch<SetStateAction<T>>] {
    const [state, updateState] = useState<T>(() => {
        const savedItem = store.getItem(key)
        if (savedItem) {
            return JSON.parse(savedItem)
        } else {
            return (typeof fallbackState == 'function' ? fallbackState() : fallbackState)
        }
    })

    // The key really ought not change, but it will be less bad if we start writing to a new location than
    // if we keep writing to the old one
    const customSetValue = useCallback((newValue: T | ((prev: T) => T)) => {
        updateState((valueWas) => {
            const computedValue = newValue instanceof Function ? newValue(valueWas) : newValue;
            store.setItem(key, JSON.stringify(computedValue));
            return computedValue;
        })
    }, [key]);

    return [state, customSetValue]
}

export function useSessionStorageState<T>(key: string, fallbackState?: Initializer<T>) {
    return usePersistedState<T>(window.sessionStorage, key, fallbackState)
}

/* quick little helper to combine id & state variables */
export function useCheckboxState(init: boolean = false) {
    const [value, setValue] = useState(init);
    const id = useId();
    return {checked: value, set: setValue, onChange: (e: ChangeEvent<HTMLInputElement>) => setValue(e.target.checked), id: id};
}

export const useDebouncedEffect = (effect: () => void, deps: any[], delay: number) => {
    useEffect(() => {
        const handler = setTimeout(() => effect(), delay);

        return () => clearTimeout(handler);
    }, [...(deps || []), delay]);
}

export {dataTableExport} from "./utils/dataTableExport"
export type { DataTableExportSelector } from "./utils/dataTableExport"
